import {
  memo,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { styled, useTheme } from '@mui/material'
import { Waypoint } from 'react-waypoint'
import {
  useHistoryCustomBack,
  useNavigate,
  usePanelHistoryTop,
} from 'weblite-router'
import { useShowAudioPlayer } from '@weblite/audio'
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react'
// import FlipMove from 'react-flip-move'
import { RoomTypes } from '@weblite/ts-action/room'
import {
  APP_BAR_HEIGHT,
  CHAT_LIST_APP_BAR_HEIGHT,
} from '$common/app-bar/app-bar.constants'
import { PageWithAppBar } from '$common/page-with-app-bar'
import { ChatListItem } from '$common/chat-list-item-new'
import { FullPageListSkeleton } from '$common/full-page-list-skeleton'
import { useMultiSelectList } from '$common/multi-select-searchable-list-page'
import {
  RoomList,
  useRoomListItems,
} from '$logic/room-list/room-list.react-hook'
import { NAVIGATION_BAR_HEIGHT } from '$common/navigation-bar'
import {
  exitRoomListFromSelectionMode,
  setRoomListToSelectionMode,
} from '$logic/router'
import { AudioPlayer } from '$common/audio-player'
import { AUDIO_PLAYER_HEIGHT } from '$common/audio-player/audio-player-constants'
import { CallBar } from '$comp/_app/components/chat/components/call-bar'
import { TopBarsContainer } from '$common/top-bars-container'
import { useShowCallBar } from '$logic/call'
import { vibrate } from '$func/vibrate'
import { UnseenChip } from '$common/chip'
import { openRoom, useOpenedRoomId } from '$logic/opened-room'
import { useRoomListUnseenCount } from '$logic/room-seen/react-hook'
import { toLocale } from '$func/text'
import { t } from '$i18n'
import { ITab, Tabs } from '$common/tabs/tabs'
import { ToolboxFab } from '$comp/_app/components/leftPanel/components/chat-list/components/toolbox-fab'
import { useIsDesktopSize } from '$common/react-hooks/layout'
import {
  INCREASE_LIMIT_OFFSET,
  useItemsRenderLimit,
  useVisitedFolders,
} from './react-hooks'
import { EmptyChatList } from './components/empty-chat-list'
import { CreateRoomFab } from './components/create-room-fab'
import { ChatAppBar, ChatAppBarSelection } from './components/chat-app-bar'
import {
  STORY_RIBBON_HEIGHT_CSS_VAR,
  STORY_RIBBON_TRANSITION_MS,
} from '../../../story/constants'
import useTopOverscroll from '$hooks/scroll/use-top-overscroll'
import { StoryRibbonsApi } from '$comp/_app/components/story/story-ribbons-api'
import { StoryRibbons } from '$comp/_app/components/story/story-ribbons'
import {
  IS_STORY_ENABLED,
  SHOULD_CATEGORIZE_CHATS,
} from '$func/environmentInfo'
import { useStateRef } from '$hooks/use-state-ref'

export const TABS_HEIGHT = SHOULD_CATEGORIZE_CHATS ? 39 : 0
export const CHAT_LIST_WRAPPER_ID = 'chatListWrapper'

function a11yProps(index: number) {
  return {
    key: `chat-list-tab-${index}`,
    'aria-controls': `chat-list-tab-panel-${index}`,
  }
}

export type ChatTypes = 'ALL' | RoomTypes

const filterByRoomType = (roomType: RoomTypes | undefined, type: ChatTypes) =>
  type === 'ALL' ||
  (type === 'PV' && roomType === 'PRIVATE_SPACE') ||
  type === roomType

export const folderChatsTypes = ['ALL', 'PV', 'CHANNEL', 'GROUP'] as ChatTypes[]

const ChatListItemWrapper = styled('div')`
  background-color: ${({ theme }) => theme.palette.neutral000};
`

const ListRenderer = memo(function ListRendererComponent({
  selectionMap,
  selectionCount,
  items,
  onItemSelectedChange,
  chatType,
  visited,
}: {
  selectionCount: number
  selectionMap: Record<string, boolean>
  items: RoomList[]
  onItemSelectedChange: (selected: boolean, chatId: string) => void
  chatType: ChatTypes
  visited: boolean
}) {
  const actualItems = (visited ? items : []) as RoomList[]
  const selectOnTapRef = useRef<boolean>(false)
  const selectionMapRef = useRef<Record<string, boolean>>({})

  useEffect(() => {
    selectionMapRef.current = selectionMap
  }, [selectionMap])

  useEffect(() => {
    selectOnTapRef.current = selectionCount > 0
  }, [selectionCount])

  const {
    itemsRenderLimit,
    increaseItemsRenderLimit,
    increaseNeeded,
  } = useItemsRenderLimit({
    totalCount: actualItems?.length ?? 0,
    calculatePermissionsOnMount: chatType === 'ALL',
    calculatePermissionsOnScroll: false,
  })

  const onClick = useCallback(
    (chatId: string) => {
      const selected = selectionMapRef.current[chatId]

      if (selected) onItemSelectedChange(false, chatId)
      else if (selectOnTapRef.current) onItemSelectedChange(true, chatId)
      else openRoom(chatId)
    },
    [onItemSelectedChange],
  )

  const onContextMenu = useCallback(
    (chatId: string) => (e: MouseEvent) => {
      e.preventDefault()
      if (!selectOnTapRef.current) onItemSelectedChange(true, chatId)
    },
    [onItemSelectedChange],
  )

  const handleLongPress = useCallback(
    (chatId: string) => () => {
      if (!selectOnTapRef.current) onItemSelectedChange(true, chatId)
    },
    [onItemSelectedChange],
  )

  const isDesktopVersion = useIsDesktopSize()
  const openedRoomId = useOpenedRoomId()

  return (
    // <FlipMove enterAnimation="fade" leaveAnimation="fade">
    <div>
      {actualItems
        ?.filter(item => filterByRoomType(item.type, chatType))
        .slice(0, itemsRenderLimit)
        .map((chatItem, index) => (
          <ChatListItemWrapper key={chatItem.chatId}>
            {increaseNeeded &&
              index === itemsRenderLimit - INCREASE_LIMIT_OFFSET - 1 && (
                <Waypoint onEnter={increaseItemsRenderLimit} />
              )}

            <ChatListItem
              {...chatItem}
              key={chatItem.chatId}
              selected={selectionMap[chatItem.chatId]}
              isOpened={isDesktopVersion && openedRoomId === chatItem.chatId}
              onClick={onClick}
              onContextMenu={onContextMenu}
              onLongPress={handleLongPress}
            />
          </ChatListItemWrapper>
        ))}
    </div>
    //  </FlipMove>
  )
})

export function ChatList() {
  const { items, loading } = useRoomListItems()
  const theme = useTheme()

  const containerRef = useRef<HTMLDivElement>(null)

  const handleShowStoryRibbon = useCallback(() => {
    StoryRibbonsApi.toggleStoryRibbons({ isShown: true })
  }, [])

  const handleHideStoryRibbon = useCallback(() => {
    StoryRibbonsApi.toggleStoryRibbons({ isShown: false })
  }, [])

  const renderedOverflowTrigger = useTopOverscroll(
    containerRef,
    handleShowStoryRibbon,
    handleHideStoryRibbon,
    !IS_STORY_ENABLED,
  )

  const sliderRef = useRef<SwiperRef>(null)
  const { showAudioPlayer } = useShowAudioPlayer()

  const [currentChatsType, setCurrentChatsType] = useState<ChatTypes>('ALL')

  // const topAndBottomBarsHeight = APP_BAR_HEIGHT + NAVIGATION_BAR_HEIGHT
  const audioPlayerHeight = showAudioPlayer ? AUDIO_PLAYER_HEIGHT : 0

  const {
    selectionMap,
    setSelection,
    resetSelection,
    selectedItems,
    hasSelection,
  } = useMultiSelectList<RoomList>({ items, selectionPrimaryKey: 'chatId' })

  const panelMode = usePanelHistoryTop('roomList')?.state?.mode
  const navigate = useNavigate()

  const panelModeRef = useStateRef(panelMode)

  const homePanelState = usePanelHistoryTop('home')?.state
  const isTop = homePanelState?.tab === 'roomList'

  useHistoryCustomBack(
    () => {
      if (hasSelection) exitRoomListFromSelectionMode()
      else return 'closeApp'
    },
    [hasSelection],
    isTop,
  )

  useEffect(() => {
    if (hasSelection) StoryRibbonsApi.toggleStoryRibbons({ isShown: false })
  }, [hasSelection])

  // NOTE: Change panel mode when user select or deselect items
  useEffect(() => {
    if (panelModeRef.current === 'SELECT' && !selectedItems.length)
      exitRoomListFromSelectionMode()
    if (panelModeRef.current !== 'SELECT' && selectedItems.length)
      setRoomListToSelectionMode()
  }, [selectedItems])

  // NOTE: Deselect items when user clicked back button
  useEffect(() => {
    if (panelMode !== 'SELECT') {
      resetSelection()
    }
  }, [panelMode, resetSelection])

  const onItemSelectedChange = useCallback(
    (selected: boolean, chatId: string) => {
      setSelection(chatId, selected)
      if (selected) vibrate()
    },
    [setSelection],
  )

  const selectionCount = useMemo(
    () => Object.values(selectionMap).filter(Boolean).length,
    [selectionMap],
  )

  const handleChatTypeChange = useCallback((newChatsType: ChatTypes) => {
    sliderRef.current?.swiper.slideTo(
      folderChatsTypes.findIndex(type => type === newChatsType),
    )
    setCurrentChatsType(newChatsType)
  }, [])

  const unseenCount = useRoomListUnseenCount()

  const tabs = useMemo<ITab<ChatTypes>[]>(() => {
    return folderChatsTypes.map((chatType, index) => ({
      label: t(`chatList.tabs.${chatType.toLowerCase()}`),
      icon: unseenCount[chatType] ? (
        <UnseenChip
          color={chatType === currentChatsType ? 'neutral800' : 'neutral000'}
          label={
            unseenCount[chatType] > 99
              ? `+${toLocale(99)}`
              : toLocale(unseenCount[chatType])
          }
          sx={{
            borderColor: 'neutral300',
            borderWidth: chatType === currentChatsType ? '0' : '1px',
            borderStyle: 'solid',
          }}
        />
      ) : undefined,
      iconPosition: 'end',
      value: chatType,
      ...a11yProps(index),
    }))
  }, [unseenCount, currentChatsType])

  const showCallBar = useShowCallBar()

  const { visitedFoldersMap, visitFolder } = useVisitedFolders()

  // const [showItems, setShowItems] = useState(true)
  // useEffect(() => {
  //   console.timeEnd('toggle')
  // }, [showItems])
  //
  // useEffect(() => {
  //   const handler = (e: any) => {
  //     if (e.code === 'KeyS') {
  //       console.time('toggle')
  //       setShowItems(show => !show)
  //       setCurrentChatsType('ALL')
  //     }
  //   }
  //
  //   window.addEventListener('keydown', handler)
  //   return () => {
  //     window.removeEventListener('keydown', handler)
  //   }
  // }, [])

  return (
    <PageWithAppBar
      bodySx={{
        position: 'relative',
        height: `calc(100% - ${
          NAVIGATION_BAR_HEIGHT + CHAT_LIST_APP_BAR_HEIGHT
        }px - ${STORY_RIBBON_HEIGHT_CSS_VAR})`,
        flexGrow: 0,
        overflow: 'hidden',
        isolation: 'isolate',
        transition: `height ${STORY_RIBBON_TRANSITION_MS}ms ease`,
      }}
      appBar={
        <>
          {hasSelection ? (
            <ChatAppBarSelection selectedItems={selectedItems} />
          ) : (
            <ChatAppBar />
          )}
          <StoryRibbons />
        </>
      }
    >
      {loading ? (
        <FullPageListSkeleton size="large" />
      ) : !items.length ? (
        <EmptyChatList />
      ) : (
        <>
          {SHOULD_CATEGORIZE_CHATS && (
            <Tabs
              tabs={tabs}
              tabsSx={{ height: TABS_HEIGHT, direction: 'ltr' }}
              tabSx={{
                color: theme.palette.neutral900,
                '&.MuiTab-root': {
                  opacity: 0.7,
                },
                '&[data-selected="true"]': {
                  opacity: 1,
                },
              }}
              value={currentChatsType}
              onChange={handleChatTypeChange}
              isLtr
            />
          )}

          <TopBarsContainer
            isStatic
            hasAnyChild={showAudioPlayer || showCallBar}
          >
            {showCallBar && <CallBar />}
            {showAudioPlayer && <AudioPlayer />}
          </TopBarsContainer>

          <div
            ref={containerRef}
            style={{
              height: '100%',
              overflow: 'hidden auto',
            }}
            id={CHAT_LIST_WRAPPER_ID}
          >
            {renderedOverflowTrigger}
            {SHOULD_CATEGORIZE_CHATS ? (
              <Swiper
                ref={sliderRef}
                autoHeight
                dir="ltr"
                touchStartPreventDefault={false}
                onSlideChange={e => {
                  const folder = folderChatsTypes[e.realIndex]
                  setCurrentChatsType(folder)
                  visitFolder(folder)
                }}
              >
                {folderChatsTypes.map(chatType => (
                  <SwiperSlide
                    key={chatType}
                    dir="rtl"
                    style={{
                      minHeight: `calc(100% - ${
                        NAVIGATION_BAR_HEIGHT +
                        CHAT_LIST_APP_BAR_HEIGHT +
                        TABS_HEIGHT +
                        audioPlayerHeight
                      }px - ${STORY_RIBBON_HEIGHT_CSS_VAR} )`,
                    }}
                  >
                    <ListRenderer
                      selectionCount={selectionCount}
                      selectionMap={selectionMap}
                      items={items}
                      onItemSelectedChange={onItemSelectedChange}
                      chatType={chatType}
                      visited={visitedFoldersMap[chatType]}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            ) : (
              <ListRenderer
                selectionCount={selectionCount}
                selectionMap={selectionMap}
                items={items}
                onItemSelectedChange={onItemSelectedChange}
                chatType={'ALL'}
                visited={visitedFoldersMap.ALL}
              />
            )}
          </div>
          <ToolboxFab />
          <CreateRoomFab />
        </>
      )}
    </PageWithAppBar>
  )
}
